import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type FeeTab          = 'periodic' | 'structures' | 'collect' | 'records' | 'ledger' | 'concessions';
export type LedgerScope     = 'person' | 'account' | 'institute';
export type LedgerKind      = 'assessed' | 'payment' | 'discount' | 'waiver' | 'late_fee' | 'refund';
export type PaymentMode     = 'Cash' | 'Cheque' | 'Online/UPI' | 'NEFT/RTGS' | 'Card' | 'DD';
export type ReceiptStatus   = 'Paid' | 'Partial' | 'Pending' | 'Overdue' | 'Cancelled' | 'Refunded';
export type ConcessionType  = 'Percentage' | 'Fixed Amount';
export type FeeFrequency    = 'One-time' | 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Annual';
export type FeePeriodKind   = 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
export type AdvancePayMode  = 'none' | 'next_month' | 'next_quarter';

export interface FeeHead {
  id:          number;
  head_code:   string;
  head_name:   string;
  category:    string;
  is_taxable:  boolean;
  is_active:   boolean;
}

export interface FeeStructureItem {
  fee_head_code:   number;
  fee_head_name: string;
  amount:        number;
  is_mandatory:  boolean;
  frequency:     FeeFrequency;
}

export interface FeeStructure {
  id:           number;
  class_name:   string;
  program_name: string;
  academic_year:string;
  items:        FeeStructureItem[];
  total_amount: number;
  is_active:    boolean;
  created_at:   string;
}

/** One installment in a fee_plan JSONB payload. */
export interface FeePlanInstallment {
  seq:        number;
  label:      string;
  months:     string[];
  start_date: string;
  last_date:  string;
}

export interface FeePeriodic {
  id:         number;
  session:    string;
  start_date: string;
  end_date:   string;
  period:     FeePeriodKind;
  grace_days: number;
  late_fee_per_day: number;
  fee_plan:   FeePlanInstallment[];
}

export const LATE_fee_head_code = -1;
export const DEFAULT_LATE_FEE_PER_DAY = 10;
export const LATE_FEE_CAP_DAYS = 30;

export interface FeeReceiptItem {
  fee_head_code: number;
  fee_head_name: string;
  due_amount:    number;
  paid_amount:   number;
  concession:    number;
  balance:       number;
  period_label?: string;
}

export interface FeeReceipt {
  id:             number;
  receipt_no:     string;
  student_id:     string;
  student_name:   string;
  class_name:     string;
  roll_no:        string;
  academic_year:  string;
  concession_id:  number;
  payment_date:   any | null;
  due_date:       any | null;
  payment_mode:   PaymentMode;
  cheque_dd_no:   string;
  bank_name:      string;
  items:          FeeReceiptItem[];
  total_due:      number;
  total_paid:     number;
  total_concession:number;
  balance:        number;
  advance:        string;
  period_seq:     number | null;
  period_label:   string;
  period_seqs:    number[];
  covers_full:    boolean;
  payment_verified: boolean;
  late_fee:       number;
  days_overdue:   number;
  refund_amount:  number;
  refund_date:    any | null;
  refund_reason:  string;
  refund_mode:    PaymentMode | '';
  status:         ReceiptStatus;
  remarks:        string;
  created_by:     string;
}

export interface Concession {
  id:              number;
  concession_name: string;
  concession_type: ConcessionType;
  c_value:         number;
  applicable_heads:string[];
  criteria:        string;
  is_active:       boolean;
}

export interface CollectFormState {
  student_id:    string;
  student_name:  string;
  class_name:    string;
  roll_no:       string;
  academic_year: string;
  payment_mode:  PaymentMode;
  cheque_dd_no:  string;
  bank_name:     string;
  payment_date:  string;
  due_date:      string;
  period_seq:    number | null;
  period_label:  string;
  advance:       AdvancePayMode;
  payment_verified: boolean;
  late_fee:      number;
  remarks:       string;
  concession_id: number | null;
  items:         CollectItem[];
}

export interface CollectItem {
  fee_head_code:   number;
  fee_head_name: string;
  frequency:     FeeFrequency;
  due_amount:    number;
  original_due:  number;
  concession:    number;
  paid_amount:   number;
  previously_paid: number;
  previous_concession: number;
  in_partial:    boolean;
  locked:        boolean;
  locked_reason: string;
  period_seq:    number;
  period_label:  string;
}

export interface PreviousPaymentInfo {
  receipt_no:        string;
  payment_date:      string;
  period_label:      string;
  total_paid:        number;
  total_due:         number;
  total_concession:  number;
  remaining:         number;
}

export interface FeeLedgerParty {
  student_id:     string;
  student_name:   string;
  class_name:     string;
  roll_no:        string;
  account_code:   string;
  account_name:   string;
  institute_name: string;
}

export interface FeeWaiver {
  id:             number;
  student_id:     string;
  student_name:   string;
  class_name:     string;
  academic_year:  string;
  waiver_date:    string;
  amount:         number;
  fee_head_name:  string;
  reason:         string;
  approved_by:    string;
}

export interface FeeLedgerEntry {
  id:               string;
  date:             string;
  kind:             LedgerKind;
  particulars:      string;
  ref_no:           string;
  student_id:       string;
  student_name:     string;
  debit:            number;
  credit:           number;
  running_balance:  number;
}

export interface FeeLedgerStatement {
  scope:          LedgerScope;
  title:          string;
  subtitle:       string;
  academic_year:  string;
  parties:        FeeLedgerParty[];
  entries:        FeeLedgerEntry[];
  assessed:       number;
  payments:       number;
  discounts:      number;
  waivers:        number;
  late_fees:      number;
  refunds:        number;
  remaining:      number;
}

export function ledgerAccountForClass(className: string): { code: string; name: string } {
  if (className.startsWith('Class IX')) return { code: 'FEE-IX', name: 'Class IX · Secondary Programme' };
  if (className.includes('XI') && className.includes('Science')) return { code: 'FEE-XI-SCI', name: 'Class XI · Science Stream' };
  if (className === 'BCA') return { code: 'FEE-BCA', name: 'BCA · Bachelor of Computer Applications' };
  return { code: 'FEE-GEN', name: className || 'General fee account' };
}

// export function ledgerInstituteForClass(className: string): string {
//   return className === 'BCA' ? 'EduSmart North Delhi Branch' : 'EduSmart Main Campus';
// }

export function parseLocalDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function toIsoDate(d: Date): string {
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

export function addDaysIso(iso: string, days: number): string {
  const d = parseLocalDate(iso);
  if (!d) return iso;
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

/** Build fee_plan JSON from a session window, period, and grace days. */
export function generateFeePlan(
  startDate: string,
  endDate:   string,
  period:    FeePeriodKind,
  graceDays: number,
): FeePlanInstallment[] {
  const start = parseLocalDate(startDate);
  const end   = parseLocalDate(endDate);
  if (!start || !end || end < start) return [];
  const grace = Math.min(20, Math.max(0, Math.trunc(Number(graceDays) || 0)));

  const months: Date[] = [];
  let y = start.getFullYear();
  let m = start.getMonth();
  const endY = end.getFullYear();
  const endM = end.getMonth();
  while (y < endY || (y === endY && m <= endM)) {
    months.push(new Date(y, m, 1));
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  if (!months.length) return [];

  const chunkSize = period === 'Monthly' ? 1
    : period === 'Quarterly' ? 3
    : period === 'Half-Yearly' ? 6
    : months.length;

  const plan: FeePlanInstallment[] = [];
  for (let i = 0; i < months.length; i += chunkSize) {
    const chunk = months.slice(i, i + chunkSize);
    const instStart = i === 0 ? startDate : toIsoDate(chunk[0]);
    const names = chunk.map(monthLabel);
    const label = period === 'Monthly' || names.length === 1
      ? names[0]
      : `${names[0]} – ${names[names.length - 1]}`;
    plan.push({
      seq:        plan.length + 1,
      label,
      months:     names,
      start_date: instStart,
      last_date:  addDaysIso(instStart, grace),
    });
  }
  return plan;
}

export function installmentForDate(plan: FeePlanInstallment[], iso: string): FeePlanInstallment | null {
  if (!plan.length || !iso) return null;
  const d = parseLocalDate(iso);
  if (d) {
    const label = monthLabel(d);
    const byMonth = plan.find(p => p.months.includes(label));
    if (byMonth) return byMonth;
  }
  for (let i = 0; i < plan.length; i++) {
    const start = plan[i].start_date;
    const next  = i + 1 < plan.length ? plan[i + 1].start_date : null;
    if (iso >= start && (!next || iso < next)) return plan[i];
  }
  return null;
}

/** Group key used to decide whether a prior collection covers the selected period. */
export function coverageBucket(frequency: FeeFrequency, inst: FeePlanInstallment, plan: FeePlanInstallment[]): string {
  const seq = inst.seq;
  const n   = Math.max(plan.length, 1);
  if (frequency === 'One-time') return 'one-time';
  if (frequency === 'Annual')   return 'annual';
  if (frequency === 'Monthly')  return `monthly-${seq}`;
  if (frequency === 'Quarterly') {
    const size = n >= 12 ? 3 : 1;
    return `quarterly-${Math.floor((seq - 1) / size)}`;
  }
  if (frequency === 'Half-Yearly') {
    const size = n >= 12 ? 6 : n >= 4 ? 2 : 1;
    return `half-${Math.floor((seq - 1) / size)}`;
  }
  return `monthly-${seq}`;
}

export function nextQuarterInstallments(cur: FeePlanInstallment, plan: FeePlanInstallment[]): FeePlanInstallment[] {
  const qSize = plan.length >= 12 ? 3 : 1;
  const nextQ = Math.floor((cur.seq - 1) / qSize) + 1;
  return plan.filter(p => Math.floor((p.seq - 1) / qSize) === nextQ);
}

/** Calendar days the payment date is after the due date (0 if on time or early). */
export function daysOverdue(paymentDate: string, dueDate: string): number {
  const pay = parseLocalDate(paymentDate);
  const due = parseLocalDate(dueDate);
  if (!pay || !due || pay <= due) return 0;
  return Math.round((pay.getTime() - due.getTime()) / 86400000);
}

/** Late fee = days overdue × per-day rate, capped at LATE_FEE_CAP_DAYS. */
export function suggestedLateFee(days: number, perDay: number, capDays = LATE_FEE_CAP_DAYS): number {
  if (days <= 0) return 0;
  const rate = Math.max(0, Math.trunc(Number(perDay) || 0));
  const capped = Math.min(Math.trunc(days), Math.max(1, capDays));
  return capped * rate;
}

@Component({
  selector:    'app-fee-management',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './fee-management.html',
  styleUrl:    './fee-management.scss',
})
export class FeeManagement implements OnInit {
  dataService = inject(DataService);
  loading = false;
  allStudents: any = [];
  std_kw: string  = '';
  user: any = {};

  activeTab       = signal<FeeTab>('periodic');
  submitted       = false;
  saveSuccess     = signal('');
  showDeleteModal = signal(false);
  deleteContext   = signal<{ type: string; id: number; name: string } | null>(null);
  showReceiptModal = signal(false);
  viewingReceipt   = signal<FeeReceipt | null>(null);
  showRefundModal  = signal(false);
  refundForm = this.blankRefund();
  refundErrors: Record<string, string> = {};

  // ── Fee Structure form ─────────────────────────────────
  structureFormMode: 'list' | 'form' = 'list';
  editingStructureId: number | null  = null;
  structureForm: Omit<FeeStructure, 'id'|'total_amount'|'created_at'> = this.blankStructure();
  structureErrors: Record<string, string> = {};

  // ── Collection form ────────────────────────────────────
  collectForm: CollectFormState = this.blankCollect();
  collectSubmitted = false;
  collectErrors: Record<string, string> = {};
  studentLoaded   = false;
  collectSuccess  = signal(false);
  editingReceiptId: number | null = null;

  // ── Concession form ────────────────────────────────────
  concessionFormMode: 'list' | 'form' = 'list';
  editingConcessionId: number | null  = null;
  concessionForm: Omit<Concession, 'id'> = this.blankConcession();

  // ── Periodic form ──────────────────────────────────────
  periodFormMode: 'list' | 'form' = 'list';
  editingPeriodId: number | null  = null;
  periodForm: Omit<FeePeriodic, 'id'> = this.blankPeriodic();
  periodErrors: Record<string, string> = {};
  periodSubmitted = false;

  // ── Filters (signals so filteredReceipts recomputes on change) ──
  recordSearch   = signal('');
  recordClass    = signal('');
  recordStatus   = signal('');
  recordYear     = signal('2026-27');
  recordDateFrom = signal('');
  recordDateTo   = signal('');

  ledgerScope     = signal<LedgerScope>('person');
  ledgerQuery     = signal('');
  ledgerPersonId  = signal('');
  ledgerAccount   = signal('');
  ledgerInstitute = signal('');
  ledgerYear      = signal('2026-27');

  // ── LOOKUPS ────────────────────────────────────────────
  readonly academicYears  = this.dataService.fin_period;
  readonly classOptions   = this.dataService.classes;
  readonly paymentModes:  PaymentMode[] = ['Cash','Online/UPI','Cheque','NEFT/RTGS','Card','DD'];
  readonly frequencies:   FeeFrequency[] = ['One-time','Monthly','Quarterly','Half-Yearly','Annual'];
  readonly concessionTypes: ConcessionType[] = ['Percentage','Fixed Amount'];
  readonly periodKinds: FeePeriodKind[] = ['Monthly','Quarterly','Half-Yearly','Yearly'];
  readonly advanceModes: { id: AdvancePayMode; label: string; hint: string }[] = [
    { id: 'none',         label: 'This Period Only', hint: 'Collect the selected month / quarter' },
    { id: 'next_month',   label: 'Next Month',       hint: 'Also pay the following installment' },
    { id: 'next_quarter', label: 'Next Quarter',     hint: 'Also pay the next quarter in advance' },
  ];
  // readonly programCatalog: { class_name: string; program_name: string }[] = [
  //   { class_name: 'Class VI',              program_name: 'Class VI – General' },
  //   { class_name: 'Class VII',             program_name: 'Class VII – General' },
  //   { class_name: 'Class VIII',            program_name: 'Class VIII – General' },
  //   { class_name: 'Class IX',              program_name: 'Secondary Programme' },
  //   { class_name: 'Class X',              program_name: 'Secondary Programme' },
  //   { class_name: 'Class XI – Science',   program_name: 'Science Stream' },
  //   { class_name: 'Class XI – Science',   program_name: 'PCM' },
  //   { class_name: 'Class XI – Science',   program_name: 'PCB' },
  //   { class_name: 'Class XI – Commerce',  program_name: 'Commerce Stream' },
  //   { class_name: 'Class XI – Arts',      program_name: 'Arts / Humanities Stream' },
  //   { class_name: 'Class XII – Science',  program_name: 'Science Stream' },
  //   { class_name: 'Class XII – Science',  program_name: 'PCM' },
  //   { class_name: 'Class XII – Science',  program_name: 'PCB' },
  //   { class_name: 'Class XII – Commerce', program_name: 'Commerce Stream' },
  //   { class_name: 'Class XII – Arts',     program_name: 'Arts / Humanities Stream' },
  //   { class_name: 'BCA',                  program_name: 'Bachelor of Computer Applications' },
  //   { class_name: 'BCA',                  program_name: 'BCA – Data Science' },
  //   { class_name: 'BBA',                  program_name: 'Bachelor of Business Administration' },
  //   { class_name: 'B.Com',                program_name: 'Bachelor of Commerce' },
  //   { class_name: 'MBA',                  program_name: 'Master of Business Administration' },
  // ];

  // ── MASTER DATA ────────────────────────────────────────
  feeHeads = signal<FeeHead[]>([
    // { id:1, head_code:'TF',   head_name:'Tuition Fee',              category:'Academic', is_taxable:false, is_active:true  },
    // { id:2, head_code:'ADM',  head_name:'Admission Fee',            category:'Academic', is_taxable:false, is_active:true  },
    // { id:3, head_code:'LIB',  head_name:'Library Fee',              category:'Academic', is_taxable:false, is_active:true  },
    // { id:4, head_code:'LAB',  head_name:'Lab / Science Fee',        category:'Academic', is_taxable:false, is_active:true  },
    // { id:5, head_code:'SPT',  head_name:'Sports & Activity Fee',    category:'Activity', is_taxable:false, is_active:true  },
    // { id:6, head_code:'TRP',  head_name:'Transport Fee',            category:'Transport',is_taxable:false, is_active:true  },
    // { id:7, head_code:'HST',  head_name:'Hostel Fee',               category:'Hostel',   is_taxable:false, is_active:true  },
    // { id:8, head_code:'EXM',  head_name:'Examination Fee',          category:'Academic', is_taxable:false, is_active:true  },
    // { id:9, head_code:'COMP', head_name:'Computer Lab Fee',         category:'Academic', is_taxable:false, is_active:true  },
    // { id:10,head_code:'MISC', head_name:'Miscellaneous',            category:'Other',    is_taxable:false, is_active:true  },
    // { id:11,head_code:'DEV',  head_name:'Development / Building Fee',category:'Infrastructure',is_taxable:false,is_active:true },
    // { id:12,head_code:'IDN',  head_name:'Identity Card Fee',        category:'Admin',    is_taxable:false, is_active:false },
  ]);

  private nextStructureId = 0;
  feeStructures = signal<FeeStructure[]>([
    // { id:1, class_name:'Class XI – Science', program_name:'Science Stream', academic_year:'2026-27', is_active:true, created_at:'2026-04-01',
    //   items:[
    //     { fee_head_code:2, fee_head_name:'Admission Fee',          amount:2000,  is_mandatory:true,  frequency:'One-time' },
    //     { fee_head_code:1, fee_head_name:'Tuition Fee',            amount:18000, is_mandatory:true,  frequency:'Annual'   },
    //     { fee_head_code:8, fee_head_name:'Examination Fee',        amount:1200,  is_mandatory:true,  frequency:'Annual'   },
    //     { fee_head_code:3, fee_head_name:'Library Fee',            amount:600,   is_mandatory:false, frequency:'Annual'   },
    //     { fee_head_code:5, fee_head_name:'Sports & Activity Fee',  amount:800,   is_mandatory:false, frequency:'Annual'   },
    //     { fee_head_code:11,fee_head_name:'Development Fee',        amount:3500,  is_mandatory:true,  frequency:'One-time' },
    //     { fee_head_code:6, fee_head_name:'Transport Fee',          amount:2500,  is_mandatory:false, frequency:'Monthly'  },
    //     { fee_head_code:4, fee_head_name:'Lab / Science Fee',      amount:1500,  is_mandatory:false, frequency:'Quarterly'},
    //   ],
    //   total_amount:30100 },
    // { id:2, class_name:'Class IX', program_name:'Secondary Programme', academic_year:'2026-27', is_active:true, created_at:'2026-04-01',
    //   items:[
    //     { fee_head_code:2, fee_head_name:'Admission Fee',          amount:1500,  is_mandatory:true,  frequency:'One-time' },
    //     { fee_head_code:1, fee_head_name:'Tuition Fee',            amount:15000, is_mandatory:true,  frequency:'Annual'   },
    //     { fee_head_code:8, fee_head_name:'Examination Fee',        amount:1000,  is_mandatory:true,  frequency:'Annual'   },
    //     { fee_head_code:3, fee_head_name:'Library Fee',            amount:500,   is_mandatory:false, frequency:'Annual'   },
    //     { fee_head_code:5, fee_head_name:'Sports & Activity Fee',  amount:600,   is_mandatory:false, frequency:'Annual'   },
    //     { fee_head_code:6, fee_head_name:'Transport Fee',          amount:1500,  is_mandatory:false, frequency:'Monthly'  },
    //   ],
    //   total_amount:20100 },
    // { id:3, class_name:'BCA', program_name:'Bachelor of Computer Applications', academic_year:'2026-27', is_active:true, created_at:'2026-04-01',
    //   items:[
    //     { fee_head_code:2, fee_head_name:'Admission Fee',          amount:5000,  is_mandatory:true,  frequency:'One-time' },
    //     { fee_head_code:1, fee_head_name:'Tuition Fee',            amount:55000, is_mandatory:true,  frequency:'Annual'   },
    //     { fee_head_code:8, fee_head_name:'Examination Fee',        amount:2400,  is_mandatory:true,  frequency:'Annual'   },
    //     { fee_head_code:9, fee_head_name:'Computer Lab Fee',       amount:3000,  is_mandatory:true,  frequency:'Annual'   },
    //     { fee_head_code:3, fee_head_name:'Library Fee',            amount:1200,  is_mandatory:false, frequency:'Annual'   },
    //     { fee_head_code:11,fee_head_name:'Development Fee',        amount:5000,  is_mandatory:true,  frequency:'One-time' },
    //     { fee_head_code:6, fee_head_name:'Transport Fee',          amount:3000,  is_mandatory:false, frequency:'Monthly'  },
    //   ],
    //   total_amount:74600 },
  ]);

  private nextReceiptId = 0;
  receipts = signal<FeeReceipt[]>([
    // this.mkReceipt(1,'RCP-2526-0001','S001','Arjun Sharma','Class XI – Science','11-A','2026-27','2026-04-05','Cash',26100,26100,0,'Paid'),
    // this.mkReceipt(2,'RCP-2526-0002','S002','Priya Mehta','Class XI – Science','11-B','2026-27','2026-04-06','Online/UPI',26100,26100,0,'Paid'),
    // this.s003PartialReceipt(),
    // this.mkReceipt(4,'RCP-2526-0004','S004','Sneha Patel','BCA','BCA-1','2026-27','2026-04-08','Cheque',71600,71600,3000,'Paid'),
    // this.mkReceipt(5,'RCP-2526-0005','S005','Vikram Singh','Class IX','9-B','2026-27','2026-04-10','Cash',18600,18600,0,'Paid'),
    // this.mkReceipt(6,'RCP-2526-0006','S006','Kavita Yadav','Class XI – Science','11-A','2026-27','2026-04-12','Online/UPI',26100,0,0,'Pending'),
    // this.mkReceipt(7,'RCP-2526-0007','S007','Mohit Gupta','BCA','BCA-1','2026-27','2026-04-14','NEFT/RTGS',71600,71600,5000,'Paid'),
    // this.mkReceipt(8,'RCP-2526-0008','S008','Anita Singh','Class IX','9-C','2026-27','2026-03-31','Cash',18600,0,0,'Overdue'),
  ]);

  private nextConcessionId = 0;
  concessions = signal<Concession[]>([
    // { id:1, concession_name:'SC/ST Concession',     concession_type:'Percentage',   value:50, applicable_heads:['Tuition Fee','Examination Fee'],  criteria:'For SC/ST category students',             is_active:true },
    // { id:2, concession_name:'Staff Ward Discount',  concession_type:'Percentage',   value:25, applicable_heads:['Tuition Fee'],                    criteria:'For children of school/college staff',    is_active:true },
    // { id:3, concession_name:'Sibling Discount',     concession_type:'Percentage',   value:10, applicable_heads:['Tuition Fee','Library Fee'],       criteria:'Second or subsequent sibling enrolled',   is_active:true },
  ]);

  waivers = signal<FeeWaiver[]>([
    // { id:1, student_id:'S008', student_name:'Anita Singh',   class_name:'Class IX',           academic_year:'2026-27', waiver_date:'2026-04-15', amount:1000, fee_head_name:'Examination Fee',        reason:'Principal waiver — financial hardship', approved_by:'Principal' },
    // { id:2, student_id:'S006', student_name:'Kavita Yadav',  class_name:'Class XI – Science', academic_year:'2026-27', waiver_date:'2026-04-20', amount:500,  fee_head_name:'Late Fee',               reason:'First-time late payment waived',        approved_by:'Accounts Officer' },
    // { id:3, student_id:'S003', student_name:'Rahul Kumar',   class_name:'Class IX',           academic_year:'2026-27', waiver_date:'2026-04-18', amount:600,  fee_head_name:'Sports & Activity Fee',  reason:'Sibling sports waiver',                 approved_by:'Vice Principal' },
  ]);

  ledgerParties = signal<FeeLedgerParty[]>([
    // this.mkParty('S001','Arjun Sharma','Class XI – Science','11-A-01'),
    // this.mkParty('S002','Priya Mehta','Class XI – Science','11-B-04'),
    // this.mkParty('S003','Rahul Kumar','Class IX','9-A-07'),
    // this.mkParty('S004','Sneha Patel','BCA','BCA-1'),
    // this.mkParty('S005','Vikram Singh','Class IX','9-B'),
    // this.mkParty('S006','Kavita Yadav','Class XI – Science','11-A'),
    // this.mkParty('S007','Mohit Gupta','BCA','BCA-1'),
    // this.mkParty('S008','Anita Singh','Class IX','9-C'),
    // this.mkParty('S009','Pooja Nair','BCA','BCA-1-09'),
    // this.mkParty('S010','Suresh Babu','Class IX','9-B-12'),
  ]);

  private nextPeriodId = 0;
  feePeriodics = signal<FeePeriodic[]>([
    // {
    //   id: 1, session: '2026-27', start_date: '2026-04-01', end_date: '2027-03-31',
    //   period: 'Monthly', grace_days: 10, late_fee_per_day: DEFAULT_LATE_FEE_PER_DAY,
    //   fee_plan: generateFeePlan('2026-04-01', '2027-03-31', 'Monthly', 10),
    // },
  ]);

  // ── Computed ───────────────────────────────────────────
  readonly filteredReceipts = computed(() => {
    const q    = this.recordSearch().toLowerCase().trim();
    const cls  = this.recordClass();
    const st   = this.recordStatus();
    const yr   = this.recordYear();
    const from = this.recordDateFrom();
    const to   = this.recordDateTo();
    return this.receipts().filter(r => {
      const mQ = !q || [r.student_name, r.receipt_no, r.student_id, r.class_name].some(v => v.toLowerCase().includes(q));
      const mC = !cls  || r.class_name    === cls;
      const mS = !st   || r.status         === st;
      const mY = !yr   || r.academic_year  === yr;
      const mD = !from || r.payment_date   >= from;
      const mE = !to   || r.payment_date   <= to;
      return mQ && mC && mS && mY && mD && mE;
    });
  });

  readonly collectionSummary = computed(() => {
    const list = this.receipts();
    const paid    = list.filter(r => r.status === 'Paid' || r.status === 'Partial');
    const pending = list.filter(r => r.status === 'Pending');
    const overdue = list.filter(r => r.status === 'Overdue');
    const refunded = list.filter(r => r.status === 'Refunded');
    const collected  = paid.reduce((s,r) => s + parseFloat(r.total_paid as any) - (parseFloat(r.refund_amount as any) || 0), 0);
    const outstanding = [...pending,...overdue].reduce((s,r) => s + r.balance, 0);
    const concessions = list.reduce((s,r) => s + parseFloat(r.total_concession as any), 0);
    return { total: list.length, paid: paid.length, pending: pending.length, overdue: overdue.length, refunded: refunded.length, collected, outstanding, concessions };
  });

  readonly ledgerAccountOptions = computed(() => {
    const map = new Map<string, string>();
    this.ledgerParties().forEach(p => map.set(p.account_code, p.account_name));
    return [...map.entries()].map(([code, name]) => ({ code, name }));
  });

  readonly ledgerInstituteOptions = computed(() =>
    [...new Set(this.ledgerParties().map(p => p.institute_name))]
  );

  readonly ledgerDirectory = computed(() => {
    const year = this.ledgerYear();
    const q = this.ledgerQuery().toLowerCase().trim();
    return this.ledgerParties()
      .filter(p => !q || [p.student_id, p.student_name, p.class_name, p.account_code, p.institute_name].some(v => v.toLowerCase().includes(q)))
      .map(p => {
        const st = this.buildPartyStatement([p], year, 'person');
        return { ...p, remaining: st.remaining, assessed: st.assessed, payments: st.payments };
      });
  });

  readonly ledgerStatement = computed(() => this.buildSelectedLedger());

  collectTotal(): {
    due: number; paid: number; concession: number; balance: number;
    late: number; days: number; payable: number;
    previousPaid: number; remainingCollectable: number;
  } {
    const items = this.collectForm.items.filter(i => !i.locked);
    const paid = items.reduce((s, i) => s + (i.paid_amount ?? 0), 0);
    const conc = items.reduce((s, i) => s + (i.concession ?? 0), 0);
    const due  = items.reduce((s, i) => s + i.due_amount, 0);
    const days = this.daysLate();
    const late = days > 0 ? Math.max(0, Number(this.collectForm.late_fee) || 0) : 0;
    const previousPaid = this.collectForm.items.reduce((s, i) => s + (i.previously_paid ?? 0), 0);
    const remainingCollectable = this.remainingCollectable();
    return { due, paid, concession: conc, balance: due - paid - conc, late, days, payable: paid + late, previousPaid, remainingCollectable };
  }

  daysLate(): number {
    return daysOverdue(this.collectForm.payment_date, this.collectForm.due_date);
  }

  lateFeeRate(): number {
    const periodic = this.feePeriodics().find(p => p.session === this.collectForm.academic_year);
    const rate = Number(periodic?.late_fee_per_day);
    return Number.isFinite(rate) && rate >= 0 ? rate : DEFAULT_LATE_FEE_PER_DAY;
  }

  onPaymentDateChange(): void {
    this.syncLateFee();
  }

  waiveLateFee(): void {
    this.collectForm.late_fee = 0;
  }

  private syncLateFee(): void {
    this.collectForm.late_fee = suggestedLateFee(this.daysLate(), this.lateFeeRate());
  }

  collectInstallments(): FeePlanInstallment[] {
    const session = this.collectForm.academic_year;
    let advMod = null;
    let periodic = null;

    if(this.collectForm.advance == 'none') {
      advMod = 'Monthly';
    } else if(this.collectForm.advance == 'next_month') {
      advMod = 'Monthly';
    } else if(this.collectForm.advance == 'next_quarter') {
      advMod = 'Quarterly';
    }

    if(advMod) {
      periodic = this.feePeriodics().find(p => p.session === session && p.period === advMod);
    } else {
      periodic = this.feePeriodics().find(p => p.session === session);
    }
    
    return periodic?.fee_plan ?? [];
  }

  selectedInstallment(): FeePlanInstallment | null {
    const seq = Number(this.collectForm.period_seq);
    if (!seq) return null;
    return this.collectInstallments().find(p => p.seq === seq) ?? null;
  }

  isNonCash(): boolean {
    return this.collectForm.payment_mode !== 'Cash';
  }

  targetPeriods(): FeePlanInstallment[] {
    const plan = this.collectInstallments();
    // const cur  = this.selectedInstallment();
    // if (!cur) return [];
    // const extra = this.collectForm.advance === 'next_month'
    //   ? plan.filter(p => p.seq === cur.seq + 1)
    //   : this.collectForm.advance === 'next_quarter'
    //     ? nextQuarterInstallments(cur, plan)
    //     : [];
    const extra = this.targetMonths.filter((obj: any) => {return obj;});
    extra.sort((a: any, b: any) => a.seq - b.seq);
    return [...extra];
  }
  targetMonths: any = [];

  canAdvanceMonth(): boolean {
    const cur = this.selectedInstallment();
    return !!cur && this.collectInstallments().some(p => p.seq === cur.seq + 1);
  }

  canAdvanceQuarter(): boolean {
    const cur = this.selectedInstallment();
    return !!cur && nextQuarterInstallments(cur, this.collectInstallments()).length > 0;
  }

  advanceSummary(): string {
    const extra = this.targetPeriods().slice(1);
    if (!extra.length) return '';
    return extra.map(p => p.label).join(', ');
  }

  async ngOnInit() {
    let erp_rem: any = localStorage.getItem("erp_rem");
    if(erp_rem) {
      erp_rem = JSON.parse(erp_rem);
      erp_rem = this.dataService.deCodeUser(erp_rem);
      this.user = erp_rem;
    }

    let fh: any = this.dataService.fee_heads;

    if(fh && fh.length) {
      for(let i=0; i<fh.length; i++) {
        const fhObj: any = { 
          id: i+1, 
          head_code: fh[i].code, 
          head_name: fh[i].name,
          category: '', 
          is_taxable: false, 
          is_active: true
        };
        this.feeHeads().push(fhObj);
      }
    }

    this.loading = true;
    await this.dataService.getAllRecords('admissions').then((res: any) => {
      if(res && res.length) {
        let ldgrp: any = [];
        for(let i=0; i<res.length; i++) {
          res[i].std_info = this.dataService.str2json(res[i].std_info);
          ldgrp.push(this.mkParty(res[i].id, res[i].std_name, res[i].class_name, res[i].roll_no));
        }

        this.allStudents = res;
        this.ledgerParties.set(ldgrp);
      }

      this.loading = false;
    });

    this.loading = true;
    await this.dataService.getAllRecords('fee_structure').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.feeStructures.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('fee_concessions').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.concessions.set(res);
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('fee_receipts').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.receipts.set(res);
        this.nextReceiptId = res.length + 1;
      } else {
        this.nextReceiptId = 1;
      }
    });

    this.loading = true;
    await this.dataService.getAllRecords('fee_periodic').then((res: any) => {
      this.loading = false;
      if(res && res.length) {
        this.feePeriodics.set(res);
      }
    });

    // this.periodForm.session = parseInt(this.recordYear().split('-')[0]) as any;
    this.collectForm.academic_year = this.recordYear();
    this.structureForm.academic_year = this.recordYear();
  }

  handleAllSelection(event: any) {
    if (event.target.checked) {
      this.targetMonths = this.collectInstallments().filter((item: any) => {
        return item;
      });
    } else {
      this.targetMonths = [];
    }

    const checkboxes = document.querySelectorAll('.cis');
    checkboxes.forEach((checkbox: any) => {
      checkbox.checked = event.target.checked;
    });

    this.targetPeriods();
    this.loadStudent();
  }

  handleSelection(event: any, targetObject: any) {
    if (event.target.checked) {
      // Checkbox checked: add the whole object to array2
      this.targetMonths.push(targetObject);
    } else {
      // Checkbox unchecked: remove the object by matching its unique sequence
      this.targetMonths = this.targetMonths.filter((item: any) => {
        return item.seq !== targetObject.seq;
      });
    }
    
    // this.targetPeriods = this.targetMonths.filter((obj: any) => {return obj});
    this.targetPeriods();
    this.loadStudent();
  }

  getItShort(pLbl: string) {
    const pLblx = pLbl.split('+').map((pl: any) => pl.trim());
    if(pLblx.length > 3) {
      return pLblx[0] + ' ... ' + pLblx[pLblx.length-1];
    } else {
      return pLbl;
    }
  }

  // ──────────────────────────────────────────────────────
  // FEE STRUCTURES
  // ──────────────────────────────────────────────────────
  openNewStructure(): void {
    this.structureForm    = this.blankStructure();
    this.structureErrors  = {};
    this.submitted        = false;
    this.editingStructureId = null;
    this.structureFormMode  = 'form';
    scrollTo(0, 0);
  }

  // programsForClass(className: string): string[] {
  //   if (!className) return [];
  //   return this.programCatalog.filter(p => p.class_name === className).map(p => p.program_name);
  // }

  // onStructureClassChange(): void {
  //   const titles = this.programsForClass(this.structureForm.class_name);
  //   this.structureForm.program_name = titles[0] ?? '';
  // }

  editStructure(s: FeeStructure): void {
    this.structureForm = { class_name: s.class_name, program_name: s.program_name, academic_year: s.academic_year, is_active: s.is_active, items: JSON.parse(JSON.stringify(s.items)) };
    this.structureErrors  = {};
    this.submitted        = false;
    this.editingStructureId = s.id;
    this.structureFormMode  = 'form';
    scrollTo(0, 0);
  }

  addStructureItem(): void {
    this.structureForm.items.push({ fee_head_code: 0, fee_head_name: '', amount: 0, is_mandatory: true, frequency: 'Annual' });
  }

  removeStructureItem(i: number): void { this.structureForm.items.splice(i, 1); }

  onHeadSelect(item: FeeStructureItem): void {
    const head = this.feeHeads().find(h => h.id === Number(item.fee_head_code));
    if (head) item.fee_head_name = head.head_name;
  }

  saveStructure(): void {
    this.submitted = true;
    this.structureErrors = {};
    if (!this.structureForm.class_name)   this.structureErrors['class_name']    = 'Class is required.';
    if (!this.structureForm.program_name) this.structureErrors['program_name']  = 'Program name / title is required.';
    if (!this.structureForm.academic_year)this.structureErrors['academic_year'] = 'Academic year is required.';
    if (!this.structureForm.items.length) this.structureErrors['items']         = 'Add at least one fee item.';
    if (Object.keys(this.structureErrors).length) return;

    const total = this.structureForm.items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const now   = new Date().toISOString().substring(0, 10);

    if (this.editingStructureId !== null) {
      this.feeStructures.update(list =>
        list.map(s => {
          if(s.id === this.editingStructureId) {
            const strForm: any = { ...s, ...this.structureForm, total_amount: total };
            this.doUpdate('fee_structure', strForm);
            return strForm;
          }
        })
      );
    } else {
      const strForm: any = {
        ...this.structureForm,
        id:           this.nextStructureId++,
        total_amount: total,
        created_at:   now,
      }
      this.doSave('fee_structure', strForm);
      this.feeStructures.update(list => [...list, strForm]);
    }

    this.structureFormMode = 'list';
    this.flash('Fee structure saved successfully!');
  }

  deleteStructure(id: number, name: string): void {
    this.deleteContext.set({ type: 'structure', id, name });
    this.showDeleteModal.set(true);
  }

  // ──────────────────────────────────────────────────────
  // FEE COLLECTION
  // ──────────────────────────────────────────────────────
  loadStudent(): void {
    if (!this.std_kw) return;
    let student = this.allStudents.filter((data: any) => {
      return data.adm_code.toLowerCase() == this.std_kw.toLowerCase() || 
        data.roll_no.toLowerCase() == this.std_kw.toLowerCase() || 
        data.std_name.toLowerCase() == this.std_kw.toLowerCase();
    });

    if (student && student.length) {
      student = student[0];

      this.collectForm.student_id = student.id;
      this.collectForm.student_name = student.std_name;
      this.collectForm.class_name = student.class_name;
      this.collectForm.roll_no = student.roll_no;
      this.loadFeeItems();
      this.studentLoaded = true;
    } else {
      this.collectForm.student_id = '';
      this.collectForm.student_name = '';
      this.collectForm.class_name = '';
      this.collectForm.roll_no = '';
      this.studentLoaded = false;
    }
  }

  loadFeeItems(): void {
    this.selectDefaultPeriod();
    this.applyConcession();
  }

  selectDefaultPeriod(): void {
    const plan = this.collectInstallments();
    this.collectForm.advance = 'none';
    if (!plan.length) {
      this.collectForm.period_seq = null;
      this.collectForm.period_label = '';
      this.rebuildCollectItems();
      return;
    }
    let chosen = plan[0];
    for (const inst of plan) {
      this.collectForm.period_seq = inst.seq;
      this.collectForm.period_label = inst.label;
      this.rebuildCollectItems();
      if (this.collectForm.items.some(i => !i.locked)) {
        chosen = inst;
        break;
      }
    }
    this.collectForm.period_seq = chosen.seq;
    this.onCollectPeriodChange();
  }

  onCollectPeriodChange(): void {
    const seq = Number(this.collectForm.period_seq);
    this.collectForm.period_seq = Number.isFinite(seq) && seq > 0 ? seq : null;
    if (this.collectForm.advance === 'next_month' && !this.canAdvanceMonth()) this.collectForm.advance = 'none';
    if (this.collectForm.advance === 'next_quarter' && !this.canAdvanceQuarter()) this.collectForm.advance = 'none';
    this.syncDueDateFromTargets();
    this.rebuildCollectItems();
    this.applyConcession();
    this.syncLateFee();
  }

  onAdvanceChange(): void {
    this.syncDueDateFromTargets();
    this.rebuildCollectItems();
    this.applyConcession();
    this.syncLateFee();
  }

  onPaymentModeChange(): void {
    this.collectForm.payment_verified = this.collectForm.payment_mode === 'Cash';
  }

  private syncDueDateFromTargets(): void {
    const targets = this.targetPeriods();
    const last = targets[targets.length - 1];
    const cur  = this.selectedInstallment();
    this.collectForm.period_label = targets.map(t => t.label).join(' + ');
    this.collectForm.due_date = last?.last_date ?? cur?.last_date ?? '';
  }

  rebuildCollectItems(): void {
    const structure = this.feeStructures().find(
      s => s.class_name === this.collectForm.class_name && s.academic_year === this.collectForm.academic_year
    );
    if (!structure) {
      this.collectForm.items = [];
      return;
    }
    const plan = this.collectInstallments();
    const periods = this.targetPeriods();
    if (!periods.length) {
      this.collectForm.items = structure.items.map(item => ({
        fee_head_code: item.fee_head_code, fee_head_name: item.fee_head_name, frequency: item.frequency,
        due_amount: item.amount, original_due: item.amount, concession: 0, paid_amount: item.amount,
        previously_paid: 0, previous_concession: 0, in_partial: false,
        locked: false, locked_reason: '', advance: 'none', period_seq: 0, period_label: '',
      }));
      this.applyHeadLocks();
      return;
    }
    const items: CollectItem[] = [];
    periods.forEach((inst, idx) => {
      structure.items.forEach(head => {
        if (idx > 0 && !this.headDueInAdvancePeriod(head.frequency, inst, plan)) return;
        items.push({
          fee_head_code: head.fee_head_code, fee_head_name: head.fee_head_name, frequency: head.frequency,
          due_amount: head.amount, original_due: head.amount, concession: 0, paid_amount: head.amount,
          previously_paid: 0, previous_concession: 0, in_partial: false,
          locked: false, locked_reason: '', period_seq: inst.seq, period_label: inst.label,
        });
      });
    });
    this.collectForm.items = items;
    this.applyHeadLocks();
  }

  private headDueInAdvancePeriod(frequency: FeeFrequency, inst: FeePlanInstallment, plan: FeePlanInstallment[]): boolean {
    if (frequency === 'One-time' || frequency === 'Annual') return false;
    if (frequency === 'Monthly') return true;
    const n = plan.length;
    if (frequency === 'Quarterly') {
      const size = n >= 12 ? 3 : 1;
      return (inst.seq - 1) % size === 0;
    }
    if (frequency === 'Half-Yearly') {
      const size = n >= 12 ? 6 : n >= 4 ? 2 : 1;
      return (inst.seq - 1) % size === 0;
    }
    return true;
  }

  applyHeadLocks(): void {
    const plan = this.collectInstallments();
    const prior = this.priorCollectReceipts();
    const bundleCredits = new Map<string, { paid: number; concession: number }>();
    for (const r of prior) {
      if (r.covers_full && r.status === 'Partial' && r.balance > 0) {
        this.allocatePartialBundle(r, this.collectForm.items, plan).forEach((credit, key) => bundleCredits.set(key, credit));
      }
    }

    this.collectForm.items.forEach(item => {
      item.locked = false;
      item.locked_reason = '';
      item.previously_paid = 0;
      item.previous_concession = 0;
      item.in_partial = false;
      item.due_amount = item.original_due;
      const itemInst = plan.find(p => p.seq === item.period_seq) ?? this.selectedInstallment();
      if (!itemInst || !plan.length) {
        if (!item.locked) item.paid_amount = Math.max(0, item.due_amount - item.concession);
        return;
      }

      let paid = 0;
      let concession = 0;
      let fullCover: FeePlanInstallment | null = null;
      for (const r of prior) {
        if (!this.receiptCoversItemPeriod(r, item, plan, itemInst)) continue;
        if (this.receiptFullyCoversHead(r)) {
          fullCover = this.receiptInstallments(r, plan)[0] ?? itemInst;
          paid = item.original_due;
          concession = 0;
          break;
        }
        const lines = r.items.filter(i => this.receiptLineMatchesHead(i, item));
        if (lines.length) {
          if (r.status === 'Partial' || lines.some(l => l.balance > 0)) item.in_partial = true;
          for (const line of lines) {
            paid += line.paid_amount;
            concession += line.concession;
          }
        } else if (r.covers_full && r.status === 'Partial') {
          const credit = bundleCredits.get(`${item.fee_head_code}-${item.period_seq}`);
          if (credit) {
            item.in_partial = true;
            paid += credit.paid;
            concession += credit.concession;
          }
        }
      }

      item.previously_paid = paid;
      item.previous_concession = concession;
      const remaining = Math.max(0, item.original_due - paid - concession);
      if (remaining <= 0) {
        item.locked = true;
        item.due_amount = item.original_due;
        item.paid_amount = 0;
        item.concession = 0;
        const when = fullCover?.label ?? item.period_label;
        item.locked_reason = item.frequency === 'One-time'
          ? `Already collected (one-time)${when ? ' in ' + when : ''}`
          : item.frequency === 'Annual'
            ? `Already collected for AY ${this.collectForm.academic_year}`
            : `Already collected in ${when || 'a previous receipt'}`;
        return;
      }
      if (paid > 0 || concession > 0 || item.in_partial) {
        item.due_amount = remaining;
        item.in_partial = true;
      }
      item.paid_amount = Math.max(0, item.due_amount - item.concession);
    });

    if (this.collectForm.items.some(i => i.in_partial && !i.locked)) {
      this.collectForm.items.forEach(i => {
        if (!i.locked && !i.in_partial) i.paid_amount = 0;
      });
    }
  }

  priorCollectReceipts(): FeeReceipt[] {
    const sid  = this.collectForm.student_id;
    const year = this.collectForm.academic_year;
    return this.receipts().filter(r =>
      r.student_id === sid &&
      r.academic_year === year &&
      r.id !== this.editingReceiptId &&
      r.status !== 'Cancelled' &&
      r.status !== 'Refunded' &&
      r.payment_verified &&
      this.netPaid(r) > 0
    );
  }

  private receiptFullyCoversHead(r: FeeReceipt): boolean {
    return r.covers_full && r.status !== 'Partial' && r.balance <= 0;
  }

  private receiptLineMatchesHead(line: FeeReceiptItem, item: CollectItem): boolean {
    if (line.fee_head_code === LATE_fee_head_code || line.fee_head_name === 'Late Fee') return false;
    return line.fee_head_code === item.fee_head_code || line.fee_head_name === item.fee_head_name;
  }

  private receiptCoversItemPeriod(
    r: FeeReceipt, item: CollectItem, plan: FeePlanInstallment[], itemInst: FeePlanInstallment,
  ): boolean {
    const paidInsts = this.receiptInstallments(r, plan);
    if (!paidInsts.length) {
      return item.frequency === 'One-time' || item.frequency === 'Annual' || r.covers_full;
    }
    return paidInsts.some(pi =>
      coverageBucket(item.frequency, pi, plan) === coverageBucket(item.frequency, itemInst, plan)
    );
  }

  private allocatePartialBundle(
    r: FeeReceipt, items: CollectItem[], plan: FeePlanInstallment[],
  ): Map<string, { paid: number; concession: number }> {
    const map = new Map<string, { paid: number; concession: number }>();
    let billLeft = r.total_due;
    let paidLeft = this.netPaid(r);
    let concLeft = r.total_concession;
    for (const item of items) {
      const itemInst = plan.find(p => p.seq === item.period_seq) ?? this.selectedInstallment();
      if (!itemInst || !this.receiptCoversItemPeriod(r, item, plan, itemInst)) continue;
      if (billLeft <= 0) break;
      const billed = Math.min(item.original_due || item.due_amount, billLeft);
      billLeft -= billed;
      const conc = Math.min(concLeft, billed);
      concLeft -= conc;
      const paid = Math.min(paidLeft, Math.max(0, billed - conc));
      paidLeft -= paid;
      map.set(`${item.fee_head_code}-${item.period_seq}`, { paid, concession: conc });
    }
    return map;
  }

  previousPaymentSummary(): { receipts: PreviousPaymentInfo[]; totalPaid: number; totalDue: number; totalConcession: number; remaining: number } | null {
    if (!this.collectForm.items.some(i => i.in_partial && !i.locked)) return null;
    const plan = this.collectInstallments();
    const prior = this.priorCollectReceipts().filter(r =>
      (r.status === 'Partial' || r.balance > 0) &&
      this.collectForm.items.some(item => {
        const itemInst = plan.find(p => p.seq === item.period_seq) ?? this.selectedInstallment();
        return !!itemInst && this.receiptCoversItemPeriod(r, item, plan, itemInst);
      })
    );
    if (!prior.length) return null;
    const receipts: PreviousPaymentInfo[] = prior.map(r => ({
      receipt_no: r.receipt_no,
      payment_date: r.payment_date,
      period_label: r.period_label,
      total_paid: r.total_paid,
      total_due: r.total_due,
      total_concession: r.total_concession,
      remaining: Math.max(0, r.balance),
    }));
    return {
      receipts,
      totalPaid: receipts.reduce((s, x) => s + x.total_paid, 0),
      totalDue: receipts.reduce((s, x) => s + x.total_due, 0),
      totalConcession: receipts.reduce((s, x) => s + x.total_concession, 0),
      remaining: receipts.reduce((s, x) => s + x.remaining, 0),
    };
  }

  hasPriorCredits(): boolean {
    return this.collectForm.items.some(i => i.previously_paid > 0 || i.previous_concession > 0 || i.in_partial);
  }

  remainingCollectable(): number {
    return this.collectForm.items
      .filter(i => !i.locked && i.in_partial)
      .reduce((s, i) => s + Math.max(0, i.due_amount - i.concession), 0);
  }

  canCollectRemaining(r: FeeReceipt): boolean {
    return r.status === 'Partial' && r.balance > 0 && r.payment_verified;
  }

  collectRemaining(): void {
    const targets = this.collectForm.items.filter(i => !i.locked && i.in_partial);
    if (targets.length) {
      this.collectForm.items.forEach(i => {
        if (i.locked) return;
        i.paid_amount = targets.includes(i) ? Math.max(0, i.due_amount - i.concession) : 0;
      });
    } else {
      this.payAll();
    }
    this.syncLateFee();
    const prev = this.previousPaymentSummary();
    if (prev && !this.collectForm.remarks) {
      this.collectForm.remarks = `Balance collection against ${prev.receipts.map(x => x.receipt_no).join(', ')}`;
    }
  }

  collectRemainingFrom(r: FeeReceipt): void {
    if (!this.canCollectRemaining(r)) return;
    this.showReceiptModal.set(false);
    this.activeTab.set('collect');
    this.editingReceiptId = null;
    this.collectForm = this.blankCollect();
    this.collectForm.student_id = r.student_id;
    this.loadStudent();
    const seq = r.period_seqs[0] ?? r.period_seq;
    if (seq) {
      this.collectForm.period_seq = seq;
      this.onCollectPeriodChange();
    }
    this.collectRemaining();
    scrollTo(0, 0);
  }

  private receiptInstallments(r: FeeReceipt, plan: FeePlanInstallment[]): FeePlanInstallment[] {
    const seqs = r.period_seqs?.length
      ? r.period_seqs
      : (r.period_seq ? [r.period_seq] : []);
    const found = seqs.map(s => plan.find(p => p.seq === s)).filter((p): p is FeePlanInstallment => !!p);
    if (found.length) return found;
    if (r.period_label) {
      const parts = r.period_label.split('+').map(s => s.trim());
      const byLabel = parts.map(l => plan.find(p => p.label === l)).filter((p): p is FeePlanInstallment => !!p);
      if (byLabel.length) return byLabel;
    }
    const one = installmentForDate(plan, r.payment_date) ?? installmentForDate(plan, r.due_date);
    return one ? [one] : [];
  }

  applyConcession(): void {
    if (!this.collectForm.concession_id) {
      this.collectForm.items.forEach(i => {
        if (!i.locked) i.concession = 0;
      });
      return;
    }
    const conc = this.concessions().find(c => c.id === Number(this.collectForm.concession_id));
    if (!conc) return;
    this.collectForm.items.forEach(i => {
      if (i.locked) return;
      if (conc.applicable_heads.includes(i.fee_head_name)) {
        const disc = conc.concession_type === 'Percentage'
          ? Math.round(i.due_amount * conc.c_value / 100)
          : conc.c_value;
        i.concession  = disc;
        i.paid_amount = Math.max(0, i.due_amount - disc);
      }
    });
  }

  payAll(): void {
    this.collectForm.items.forEach(i => {
      if (!i.locked) i.paid_amount = Math.max(0, i.due_amount - i.concession);
    });
  }

  clearAll(): void {
    this.collectForm.items.forEach(i => {
      if (!i.locked) i.paid_amount = 0;
    });
  }

  submitCollection(): void {
    this.collectSubmitted = true;
    this.collectErrors = {};
    if (!this.collectForm.student_id)  this.collectErrors['student_id']  = 'Student ID required.';
    if (!this.collectForm.student_name)this.collectErrors['student_name']= 'Student not found.';
    if (!this.collectForm.payment_date)this.collectErrors['payment_date']= 'Payment date required.';
    if (this.collectTotal().payable <= 0) this.collectErrors['amount']   = 'Paid amount must be greater than zero.';
    if (Object.keys(this.collectErrors).length) return;

    const tot  = this.collectTotal();
    const verified = this.collectForm.payment_mode === 'Cash' || this.collectForm.payment_verified;
    const rno  = `RCP-2526-${String(this.nextReceiptId).padStart(4, '0')}`;
    const collectable = this.collectForm.items.filter(i =>
      !i.locked && ((i.paid_amount ?? 0) > 0 || (i.concession ?? 0) > 0)
    );
    const itemsDue  = collectable.reduce((s, i) => s + i.due_amount, 0);
    const itemsPaid = collectable.reduce((s, i) => s + (i.paid_amount ?? 0), 0);
    const itemsConc = collectable.reduce((s, i) => s + (i.concession ?? 0), 0);
    const balance = itemsDue - itemsPaid - itemsConc;
    const status: ReceiptStatus = !verified
      ? 'Pending'
      : balance <= 0 ? 'Paid' : itemsPaid > 0 ? 'Partial' : 'Pending';
    const periodSeqs = [...new Set(collectable.map(i => i.period_seq).filter(s => s > 0))];
    const periodLabel = this.collectForm.period_label
      || [...new Set(collectable.map(i => i.period_label).filter(Boolean))].join(' + ');
    const items: FeeReceiptItem[] = collectable.map(i => ({
      fee_head_code:   i.fee_head_code,
      fee_head_name: i.fee_head_name,
      due_amount:    i.due_amount,
      paid_amount:   i.paid_amount,
      concession:    i.concession,
      balance:       i.due_amount - i.paid_amount - i.concession,
      period_label:  i.period_label,
    }));
    if (tot.late > 0) {
      items.push({
        fee_head_code: LATE_fee_head_code, fee_head_name: 'Late Fee',
        due_amount: tot.late, paid_amount: tot.late, concession: 0, balance: 0,
        period_label: `${tot.days} day${tot.days === 1 ? '' : 's'} overdue`,
      });
    }

    const existing = this.editingReceiptId !== null
      ? this.receipts().find(r => r.id === this.editingReceiptId)
      : undefined;
    const payable = itemsPaid + tot.late;
    const refundAmt = existing ? Math.min(existing.refund_amount || 0, payable) : 0;

    const newReceipt: FeeReceipt = {
      id:               existing?.id ?? this.nextReceiptId++,
      receipt_no:       existing?.receipt_no ?? rno,
      student_id:       this.collectForm.student_id,
      student_name:     this.collectForm.student_name,
      class_name:       this.collectForm.class_name,
      roll_no:          this.collectForm.roll_no,
      academic_year:    this.collectForm.academic_year,
      concession_id:    this.collectForm.concession_id || 0,
      payment_date:     this.collectForm.payment_date,
      due_date:         this.collectForm.due_date || this.collectForm.payment_date,
      advance:          this.collectForm.advance,
      period_seq:       this.collectForm.period_seq,
      period_label:     periodLabel,
      period_seqs:      periodSeqs,
      covers_full:      false,
      payment_mode:     this.collectForm.payment_mode,
      payment_verified: verified,
      cheque_dd_no:     this.collectForm.cheque_dd_no,
      bank_name:        this.collectForm.bank_name,
      items,
      total_due:        itemsDue + tot.late,
      total_paid:       payable,
      total_concession: itemsConc,
      balance,
      status:           refundAmt >= payable ? 'Refunded' : status,
      remarks:          this.collectForm.remarks,
      created_by:       existing?.created_by ?? 'Admin User',
      late_fee:         tot.late,
      days_overdue:     tot.days,
      refund_amount:    refundAmt,
      refund_date:      existing?.refund_date ?? null,
      refund_reason:    existing?.refund_reason ?? '',
      refund_mode:      existing?.refund_mode ?? '',
    };

    if (existing) {
      (newReceipt.items as any) = JSON.stringify(newReceipt.items);
      this.doUpdate('fee_receipts', newReceipt);
      this.receipts.update(list => list.map(r => r.id === existing.id ? newReceipt : r));
      this.flash('Fee collection updated.');
    } else {
      (newReceipt.items as any) = JSON.stringify(newReceipt.items);
      this.doSave('fee_receipts', newReceipt);
      this.receipts.update(list => [newReceipt, ...list]);
      this.collectSuccess.set(true);
      setTimeout(() => this.collectSuccess.set(false), 3000);
    }

    newReceipt.items = JSON.parse(newReceipt.items as any);
    this.viewingReceipt.set(newReceipt);
    this.showReceiptModal.set(true);
    this.resetCollect();
  }

  resetCollect(): void {
    this.collectForm = this.blankCollect();
    this.studentLoaded = false;
    this.collectSubmitted = false;
    this.editingReceiptId = null;
  }

  canEditCollection(r: FeeReceipt): boolean {
    return r.status !== 'Cancelled' && r.status !== 'Refunded';
  }

  editingReceiptNo(): string {
    if (this.editingReceiptId === null) return '';
    return this.receipts().find(r => r.id === this.editingReceiptId)?.receipt_no ?? '';
  }

  netPaid(r: FeeReceipt): number {
    return Math.max(0, r.total_paid - (r.refund_amount || 0));
  }

  editCollection(r: FeeReceipt): void {
    if (!this.canEditCollection(r)) return;
    this.showReceiptModal.set(false);
    this.activeTab.set('collect');
    this.editingReceiptId = r.id;
    this.studentLoaded = true;
    this.collectSubmitted = false;
    this.collectErrors = {};
    r.days_overdue = Number(r.days_overdue);
    // r.late_fee = Number(r.late_fee);
    r.period_seq = Number(r.period_seq);

    this.collectForm = {
      student_id: r.student_id,
      student_name: r.student_name,
      class_name: r.class_name,
      roll_no: r.roll_no,
      academic_year: r.academic_year,
      payment_mode: r.payment_mode,
      cheque_dd_no: r.cheque_dd_no,
      bank_name: r.bank_name,
      payment_date: r.payment_date,
      due_date: r.due_date,
      advance: (r.advance as any) || 'none', 
      period_seq: r.period_seq,
      period_label: r.period_label,
      payment_verified: r.payment_verified,
      late_fee: r.late_fee,
      remarks: r.remarks,
      concession_id: r.concession_id,
      items: (r.items as any) || [],
    };
    // const extra = (r.period_seqs || []).filter(s => s !== this.collectForm.period_seq);
    // if (extra.length === 1 && extra[0] === (this.collectForm.period_seq ?? 0) + 1) {
    //   this.collectForm.advance = 'next_month';
    // } else if (extra.length) {
    //   const cur = this.selectedInstallment();
    //   if (cur) {
    //     const q = nextQuarterInstallments(cur, this.collectInstallments()).map(p => p.seq);
    //     if (q.length && extra.every(s => q.includes(s)) && q.every(s => extra.includes(s))) {
    //       this.collectForm.advance = 'next_quarter';
    //     }
    //   }
    // }
    this.rebuildCollectItems();
    this.collectForm.items.forEach(item => {
      const src = r.items.find(i =>
        i.fee_head_code !== LATE_fee_head_code &&
        i.fee_head_name === item.fee_head_name &&
        (i.period_label === item.period_label || !i.period_label)
      );
      if (src) {
        item.paid_amount = src.paid_amount;
        item.concession = src.concession;
      }
    });
    this.collectForm.late_fee = r.late_fee;
    this.collectForm.due_date = r.due_date;
    this.collectForm.period_label = r.period_label;
    scrollTo(0, 0);
  }

  deleteCollection(r: FeeReceipt): void {
    this.showReceiptModal.set(false);
    this.deleteContext.set({ type: 'collection', id: r.id, name: `${r.receipt_no} · ${r.student_name}` });
    this.showDeleteModal.set(true);
  }

  // ──────────────────────────────────────────────────────
  // RECORDS
  // ──────────────────────────────────────────────────────
  viewReceipt(r: FeeReceipt): void { this.viewingReceipt.set(r); this.showReceiptModal.set(true); }

  cancelReceipt(id: number): void {
    const rec = this.receipts().find(r => r.id === id);
    if (!rec || rec.status === 'Cancelled' || rec.status === 'Refunded') return;
    if (!confirm('Cancel this receipt? This cannot be undone.')) return;
    this.receipts.update(list => list.map(r => r.id === id ? { ...r, status: 'Cancelled' as ReceiptStatus } : r));
  }

  verifyPayment(id: number): void {
    this.receipts.update(list => list.map(r => {
      if (r.id !== id) return r;
      if (r.status === 'Cancelled' || r.status === 'Refunded') return r;
      const paidStatus: ReceiptStatus = r.balance <= 0 ? 'Paid' : r.total_paid > 0 ? 'Partial' : 'Pending';
      return { ...r, payment_verified: true, status: paidStatus };
    }));
    const updated = this.receipts().find(r => r.id === id);
    if (updated && this.viewingReceipt()?.id === id) this.viewingReceipt.set(updated);
    this.flash('Payment marked as received.');
  }

  canRefund(r: FeeReceipt): boolean {
    return r.total_paid > 0 &&
      r.status !== 'Cancelled' &&
      r.status !== 'Refunded' &&
      (r.refund_amount || 0) < r.total_paid;
  }

  refundRemaining(r: FeeReceipt): number {
    return Math.max(0, r.total_paid - (r.refund_amount || 0));
  }

  openRefund(r: FeeReceipt): void {
    if (!this.canRefund(r)) return;
    this.refundForm = {
      id: r.id,
      amount: this.refundRemaining(r),
      date: new Date().toISOString().substring(0, 10),
      reason: '',
      mode: r.payment_mode,
    };
    this.refundErrors = {};
    this.showRefundModal.set(true);
  }

  submitRefund(): void {
    this.refundErrors = {};
    const rec = this.receipts().find(r => r.id === this.refundForm.id);
    if (!rec || !this.canRefund(rec)) {
      this.refundErrors['amount'] = 'This receipt cannot be refunded.';
      return;
    }
    const remaining = this.refundRemaining(rec);
    const amount = Number(this.refundForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) this.refundErrors['amount'] = 'Refund amount must be greater than zero.';
    else if (amount > remaining) this.refundErrors['amount'] = `Cannot refund more than ${this.formatCurrency(remaining)}.`;
    if (!this.refundForm.date) this.refundErrors['date'] = 'Refund date is required.';
    if (!this.refundForm.reason.trim()) this.refundErrors['reason'] = 'Refund reason is required.';
    if (Object.keys(this.refundErrors).length) return;

    const totalRefunded = (rec.refund_amount || 0) + amount;
    const full = totalRefunded >= rec.total_paid;

    this.receipts.update(list => list.map(r => {
      if (r.id !== rec.id) return r;
      return {
        ...r,
        status: full ? 'Refunded' as ReceiptStatus : r.status,
        refund_amount: totalRefunded,
        refund_date: this.refundForm.date,
        refund_reason: this.refundForm.reason.trim(),
        refund_mode: this.refundForm.mode,
        balance: r.total_due - (r.total_paid - totalRefunded) - r.total_concession,
      };
    }));
    const updated = this.receipts().find(r => r.id === rec.id);
    if (updated && this.viewingReceipt()?.id === rec.id) this.viewingReceipt.set(updated);
    this.showRefundModal.set(false);
    this.flash(full ? 'Fee refunded. Heads are unlocked for re-collection.' : 'Partial refund recorded.');
  }

  // ──────────────────────────────────────────────────────
  // CONCESSIONS
  // ──────────────────────────────────────────────────────
  openNewConcession(): void {
    this.concessionForm   = this.blankConcession();
    this.editingConcessionId = null;
    this.concessionFormMode  = 'form';
  }

  editConcession(c: Concession): void {
    this.concessionForm = { ...c };
    delete (this.concessionForm as any).id;
    this.editingConcessionId = c.id;
    this.concessionFormMode  = 'form';
  }

  saveConcession(): void {
    if (!this.concessionForm.concession_name) return;
    const now = new Date().toISOString();
    if (this.editingConcessionId !== null) {
      this.concessions.update(list =>
        list.map(c => c.id === this.editingConcessionId ? { ...this.concessionForm, id: c.id } : c)
      );
      (this.concessionForm as any).id = this.editingConcessionId;
      this.doUpdate('fee_concessions', this.concessionForm);
    } else {
      this.doSave('fee_concessions', this.concessionForm);
      this.concessions.update(list => [...list, { ...this.concessionForm, id: this.nextConcessionId++ }]);
    }
    this.concessionFormMode = 'list';
    this.flash('Concession saved!');
  }

  toggleConcessionStatus(c: Concession): void {
    this.concessions.update(list => list.map(x => x.id === c.id ? { ...x, is_active: !x.is_active } : x));
  }

  deleteConcession(id: number, name: string): void {
    this.deleteContext.set({ type: 'concession', id, name });
    this.showDeleteModal.set(true);
  }

  // ──────────────────────────────────────────────────────
  // FEE PERIODIC
  // ──────────────────────────────────────────────────────
  openNewPeriodic(): void {
    this.periodForm       = this.blankPeriodic();
    this.periodErrors     = {};
    this.periodSubmitted  = false;
    this.editingPeriodId  = null;
    this.periodFormMode   = 'form';
    scrollTo(0, 0);
  }

  editPeriodic(p: FeePeriodic): void {
    this.periodForm = {
      session: p.session, start_date: p.start_date, end_date: p.end_date,
      period: p.period, grace_days: p.grace_days,
      late_fee_per_day: p.late_fee_per_day ?? DEFAULT_LATE_FEE_PER_DAY,
      fee_plan: JSON.parse(JSON.stringify(p.fee_plan)),
    };
    this.periodErrors     = {};
    this.periodSubmitted  = false;
    this.editingPeriodId  = p.id;
    this.periodFormMode   = 'form';
    scrollTo(0, 0);
  }

  refreshFeePlan(): void {
    const g = Number(this.periodForm.grace_days);
    this.periodForm.grace_days = Number.isFinite(g) ? g : 0;
    this.periodForm.fee_plan = generateFeePlan(
      this.periodForm.start_date,
      this.periodForm.end_date,
      this.periodForm.period,
      this.periodForm.grace_days,
    );
  }

  savePeriodic(): void {
    this.periodSubmitted = true;
    this.periodErrors = {};
    if (!this.periodForm.session)    this.periodErrors['session']    = 'Session is required.';
    if (!this.periodForm.start_date) this.periodErrors['start_date'] = 'Start date is required.';
    if (!this.periodForm.end_date)   this.periodErrors['end_date']   = 'End date is required.';
    if (!this.periodForm.period)     this.periodErrors['period']     = 'Period is required.';
    const grace = Number(this.periodForm.grace_days);
    if (!Number.isInteger(grace) || grace < 1 || grace > 20) {
      this.periodErrors['grace_days'] = 'Grace days must be a number from 1 to 20.';
    } else {
      this.periodForm.grace_days = grace;
    }
    const lateRate = Number(this.periodForm.late_fee_per_day);
    if (!Number.isInteger(lateRate) || lateRate < 0 || lateRate > 500) {
      this.periodErrors['late_fee_per_day'] = 'Late fee per day must be a number from 0 to 500.';
    } else {
      this.periodForm.late_fee_per_day = lateRate;
    }
    if (this.periodForm.start_date && this.periodForm.end_date && this.periodForm.end_date < this.periodForm.start_date) {
      this.periodErrors['end_date'] = 'End date must be on or after start date.';
    }
    this.refreshFeePlan();
    if (!this.periodForm.fee_plan.length && !this.periodErrors['end_date'] && !this.periodErrors['start_date']) {
      this.periodErrors['fee_plan'] = 'Could not generate a fee plan from the selected dates.';
    }
    if (Object.keys(this.periodErrors).length) return;

    if (this.editingPeriodId !== null) {
      this.feePeriodics.update(list =>
        list.map(p => p.id === this.editingPeriodId ? { ...this.periodForm, id: p.id } : p)
      );
    } else {
      this.doSave('fee_periodic', this.periodForm);
      this.feePeriodics.update(list => [...list, { ...this.periodForm, id: this.nextPeriodId++ }]);
    }
    this.periodFormMode = 'list';
    this.flash('Fee period saved successfully!');
  }

  deletePeriodic(id: number, name: string): void {
    this.deleteContext.set({ type: 'periodic', id, name });
    this.showDeleteModal.set(true);
  }

  feePlanJson(): string {
    return JSON.stringify(this.periodForm.fee_plan, null, 2);
  }

  // ──────────────────────────────────────────────────────
  // DELETE CONFIRMATION
  // ──────────────────────────────────────────────────────
  executeDelete(): void {
    const ctx = this.deleteContext();
    if (!ctx) return;
    if (ctx.type === 'structure') {
      this.doDelete('fee_structure', ctx.id);
      this.feeStructures.update(list => list.filter(s => s.id !== ctx.id));
    }
    if (ctx.type === 'concession') {
      this.doDelete('fee_concessions', ctx.id);
      this.concessions.update(list => list.filter(c => c.id !== ctx.id));
    }
    if (ctx.type === 'periodic') {
      this.doDelete('fee_periodic', ctx.id);
      this.feePeriodics.update(list => list.filter(p => p.id !== ctx.id));
    }
    if (ctx.type === 'collection') {
      this.doDelete('fee_receipts', ctx.id);
      this.receipts.update(list => list.filter(s => s.id !== ctx.id));
      if (this.viewingReceipt()?.id === ctx.id) {
        this.showReceiptModal.set(false);
        this.viewingReceipt.set(null);
      }
      if (this.editingReceiptId === ctx.id) this.resetCollect();
    }
    this.showDeleteModal.set(false);
    this.deleteContext.set(null);
  }

  deleteEntityLabel(): string {
    const t = this.deleteContext()?.type;
    return t === 'structure' ? 'Fee Structure' : t === 'periodic' ? 'Fee Period' : t === 'collection' ? 'Fee Collection' : 'Concession';
  }

  // ──────────────────────────────────────────────────────
  // FEE LEDGER
  // ──────────────────────────────────────────────────────
  setLedgerScope(scope: LedgerScope): void {
    this.ledgerScope.set(scope);
    if (scope !== 'person') this.ledgerPersonId.set('');
    if (scope !== 'account') this.ledgerAccount.set('');
    if (scope !== 'institute') this.ledgerInstitute.set('');
  }

  loadLedgerPerson(): void {
    const q = this.ledgerQuery();   //.trim().toUpperCase();
    if (!q) {
      this.ledgerPersonId.set('');
      return;
    }
    const party = this.ledgerParties().find(p =>
      p.student_id === q ||
      p.student_name.toUpperCase().includes(this.ledgerQuery().trim().toUpperCase())
    );
    this.ledgerPersonId.set(party?.student_id ?? q);
  }

  openLedgerPerson(studentId: string): void {
    this.ledgerScope.set('person');
    this.ledgerPersonId.set(studentId);
    this.ledgerQuery.set(studentId);
    this.activeTab.set('ledger');
    scrollTo(0, 0);
  }

  collectFromLedger(studentId: string): void {
    this.collectForm.student_id = studentId;
    this.loadStudent();
    this.activeTab.set('collect');
    scrollTo(0, 0);
  }

  printLedger(): void {
    document.body.classList.add('print-fee-ledger');
    window.print();
    setTimeout(() => document.body.classList.remove('print-fee-ledger'), 400);
  }

  ledgerKindLabel(kind: LedgerKind): string {
    return { assessed:'Assessed', payment:'Payment', discount:'Discount', waiver:'Waiver', late_fee:'Late fee', refund:'Refund' }[kind];
  }

  ledgerKindClass(kind: LedgerKind): string {
    return { assessed:'lk-assessed', payment:'lk-payment', discount:'lk-discount', waiver:'lk-waiver', late_fee:'lk-late', refund:'lk-refund' }[kind];
  }

  private buildSelectedLedger(): FeeLedgerStatement | null {
    const year = this.ledgerYear();
    const scope = this.ledgerScope();
    let parties: FeeLedgerParty[] = [];
    let title = '';
    let subtitle = '';
    if (scope === 'person') {
      const id = this.ledgerPersonId();
      if (!id) return null;
      parties = this.ledgerParties().filter(p => p.student_id === id);
      if (!parties.length) return null;
      const p = parties[0];
      title = p.student_name;
      subtitle = `Roll No.: ${p.student_id} · ` + this.dataService.getClassName(p.class_name) + ` Class · ${p.account_code} · ${p.institute_name}`;
    } else if (scope === 'account') {
      const code = this.ledgerAccount();
      if (!code) return null;
      parties = this.ledgerParties().filter(p => p.account_code === code);
      if (!parties.length) return null;
      title = parties[0].account_name;
      subtitle = `${code} · ${parties.length} student${parties.length === 1 ? '' : 's'} · ${parties[0].institute_name}`;
    } else {
      const inst = this.ledgerInstitute();
      if (!inst) return null;
      parties = this.ledgerParties().filter(p => p.institute_name === inst);
      if (!parties.length) return null;
      title = inst;
      subtitle = `Institute fee ledger · ${parties.length} student${parties.length === 1 ? '' : 's'}`;
    }
    return this.buildPartyStatement(parties, year, scope, title, subtitle);
  }

  private buildPartyStatement(
    parties: FeeLedgerParty[],
    year: string,
    scope: LedgerScope,
    title = '',
    subtitle = '',
  ): FeeLedgerStatement {
    type Raw = Omit<FeeLedgerEntry, 'running_balance'>;
    const raw: Raw[] = [];
    const rank: Record<LedgerKind, number> = { assessed:0, late_fee:1, discount:2, waiver:3, payment:4, refund:5 };
    const ids = new Set(parties.map(p => p.student_id));

    parties.forEach(p => {
      const structure = this.feeStructures().find(s => s.class_name === p.class_name && s.academic_year === year);
      if (structure && scope === 'person') {
        structure.items.forEach((item, idx) => {
          raw.push({
            id: `a-${p.student_id}-${item.fee_head_code}-${idx}`,
            date: structure.created_at || `${year.slice(0,4)}-04-01`,
            kind: 'assessed',
            particulars: `${item.fee_head_name} assessed (${item.frequency})`,
            ref_no: structure.program_name,
            student_id: p.student_id,
            student_name: p.student_name,
            debit: item.amount,
            credit: 0,
          });
        });
      } else if (structure) {
        raw.push({
          id: `a-${p.student_id}`,
          date: structure.created_at || `${year.slice(0,4)}-04-01`,
          kind: 'assessed',
          particulars: `Fee assessed — ${p.student_name} (${p.class_name})`,
          ref_no: p.student_id,
          student_id: p.student_id,
          student_name: p.student_name,
          debit: structure.total_amount,
          credit: 0,
        });
      }
    });

    this.receipts().forEach(r => {
      if (!ids.has(r.student_id) || r.academic_year !== year || r.status === 'Cancelled') return;
      if (r.total_concession > 0) {
        raw.push({
          id: `d-${r.id}`, date: r.payment_date, kind: 'discount',
          particulars: `Discount / concession${r.period_label ? ' · ' + r.period_label : ''}`,
          ref_no: r.receipt_no, student_id: r.student_id, student_name: r.student_name,
          debit: 0, credit: r.total_concession,
        });
      }
      if (r.late_fee > 0) {
        raw.push({
          id: `l-${r.id}`, date: r.payment_date, kind: 'late_fee',
          particulars: `Late fee (${r.days_overdue} day${r.days_overdue === 1 ? '' : 's'} overdue)`,
          ref_no: r.receipt_no, student_id: r.student_id, student_name: r.student_name,
          debit: r.late_fee, credit: 0,
        });
      }
      const paidHeads = Math.max(0, (r.payment_verified ? r.total_paid : 0) - (r.late_fee || 0));
      if (paidHeads > 0) {
        raw.push({
          id: `p-${r.id}`, date: r.payment_date, kind: 'payment',
          particulars: `Payment received · ${r.payment_mode}${r.period_label ? ' · ' + r.period_label : ''}`,
          ref_no: r.receipt_no, student_id: r.student_id, student_name: r.student_name,
          debit: 0, credit: paidHeads,
        });
      }
      if (r.late_fee > 0 && r.payment_verified && r.total_paid > 0) {
        raw.push({
          id: `pl-${r.id}`, date: r.payment_date, kind: 'payment',
          particulars: 'Late fee paid',
          ref_no: r.receipt_no, student_id: r.student_id, student_name: r.student_name,
          debit: 0, credit: r.late_fee,
        });
      }
      if ((r.refund_amount || 0) > 0) {
        raw.push({
          id: `r-${r.id}`, date: r.refund_date || r.payment_date, kind: 'refund',
          particulars: `Refund${r.refund_reason ? ' — ' + r.refund_reason : ''}`,
          ref_no: r.receipt_no, student_id: r.student_id, student_name: r.student_name,
          debit: r.refund_amount, credit: 0,
        });
      }
    });

    this.waivers().forEach(w => {
      if (!ids.has(w.student_id) || w.academic_year !== year) return;
      raw.push({
        id: `w-${w.id}`, date: w.waiver_date, kind: 'waiver',
        particulars: `Waiver — ${w.fee_head_name} (${w.reason})`,
        ref_no: w.approved_by, student_id: w.student_id, student_name: w.student_name,
        debit: 0, credit: w.amount,
      });
    });

    raw.sort((a, b) => a.date.localeCompare(b.date) || rank[a.kind] - rank[b.kind] || a.id.localeCompare(b.id));
    let running = 0;
    const entries: FeeLedgerEntry[] = raw.map(e => {
      running += e.debit - e.credit;
      return { ...e, running_balance: running };
    });
    const sum = (kind: LedgerKind, field: 'debit' | 'credit') =>
      entries.filter(e => e.kind === kind).reduce((s, e) => s + parseFloat(e[field] as any), 0);
    const assessed = sum('assessed', 'debit');
    const payments = sum('payment', 'credit');
    const discounts = sum('discount', 'credit');
    const waivers = sum('waiver', 'credit');
    const late_fees = sum('late_fee', 'debit');
    const refunds = sum('refund', 'debit');
    const remaining = Math.max(0, running);
    return {
      scope, title: title || parties[0]?.student_name || 'Fee ledger',
      subtitle, academic_year: year, parties, entries,
      assessed, payments, discounts, waivers, late_fees, refunds, remaining,
    };
  }

  // ──────────────────────────────────────────────────────
  // HELPERS
  // ──────────────────────────────────────────────────────
  getStatusClass(s: ReceiptStatus): string {
    return { Paid:'st-paid', Partial:'st-partial', Pending:'st-pending', Overdue:'st-overdue', Cancelled:'st-cancelled', Refunded:'st-refunded' }[s] ?? '';
  }

  getStatusIcon(s: ReceiptStatus): string {
    return { Paid:'bi-check-circle-fill', Partial:'bi-circle-half', Pending:'bi-clock-fill', Overdue:'bi-exclamation-triangle-fill', Cancelled:'bi-x-circle-fill', Refunded:'bi-arrow-counterclockwise' }[s] ?? 'bi-circle';
  }

  getModeIcon(m: PaymentMode): string {
    return { Cash:'bi-cash-coin', 'Online/UPI':'bi-phone-fill', Cheque:'bi-file-text', 'NEFT/RTGS':'bi-bank2', Card:'bi-credit-card-2-front-fill', DD:'bi-file-earmark-text' }[m] ?? 'bi-cash';
  }

  formatCurrency(n: number): string {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
  }

  formatDate(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  resetRecordFilters(): void {
    this.recordSearch.set('');
    this.recordClass.set('');
    this.recordStatus.set('');
    this.recordYear.set('2026-27');
    this.recordDateFrom.set('');
    this.recordDateTo.set('');
  }

  printReceipt(): void { window.print(); }

  readonly structureFormTotal = computed(() =>
    this.structureForm.items.reduce((s, i) => s + (Number(i.amount) || 0), 0)
  );

  toggleConcessionHead(headName: string): void {
    const idx = this.concessionForm.applicable_heads.indexOf(headName);
    if (idx >= 0) this.concessionForm.applicable_heads.splice(idx, 1);
    else          this.concessionForm.applicable_heads.push(headName);
  }

  readonly sumItems = (_: number, i: FeeStructureItem) => _ + (Number(i.amount) || 0);

  readonly addAnnual = (_: number, m: { annualPrice: number }) => _ + m.annualPrice;

  private flash(msg: string): void {
    this.saveSuccess.set(msg);
    setTimeout(() => this.saveSuccess.set(''), 2500);
  }

  blankStructure(): Omit<FeeStructure,'id'|'total_amount'|'created_at'> {
    return { class_name:'', program_name:'', academic_year:'2026-27', is_active:true, items:[] };
  }

  blankPeriodic(): Omit<FeePeriodic, 'id'> {
    const start = '2026-04-01';
    const end   = '2027-03-31';
    const period: FeePeriodKind = 'Monthly';
    const grace = 9;
    return {
      session: '2026-27', start_date: start, end_date: end, period, grace_days: grace,
      late_fee_per_day: DEFAULT_LATE_FEE_PER_DAY,
      fee_plan: generateFeePlan(start, end, period, grace),
    };
  }

  blankCollect(): CollectFormState {
    return {
      student_id:'', student_name:'', class_name:'', roll_no:'',
      academic_year:'2026-27', payment_mode:'Cash', cheque_dd_no:'', bank_name:'',
      payment_date: new Date().toISOString().substring(0,10),
      due_date:'', advance: 'none', period_seq: null, period_label:'',
      payment_verified: true, late_fee: 0,
      remarks:'', concession_id: null, items:[],
    };
  }

  blankRefund(): { id: number; amount: number; date: string; reason: string; mode: PaymentMode } {
    return { id: 0, amount: 0, date: '', reason: '', mode: 'Cash' };
  }

  blankConcession(): Omit<Concession,'id'> {
    return { concession_name:'', concession_type:'Percentage', c_value:0, applicable_heads:[], criteria:'', is_active:true };
  }

  private mkParty(student_id: string, student_name: string, class_name: string, roll_no: string): FeeLedgerParty {
    const account = ledgerAccountForClass(class_name);
    return {
      student_id, student_name, class_name, roll_no,
      account_code: account.code, account_name: account.name,
      institute_name: this.user.institute.inst_name,
    };
  }

  // private mkReceipt(id: number, rno: string, sid: string, sname: string,
  //   cls: string, roll: string, yr: string, cons_id: number, date: string, 
  //   mode: PaymentMode, due: number, paid: number, conc: number, status: ReceiptStatus): FeeReceipt {
  //   const inApril = date.startsWith('2026-04');
  //   return {
  //     id, receipt_no: rno, student_id: sid, student_name: sname,
  //     class_name: cls, roll_no: roll, academic_year: yr, concession_id: cons_id,
  //     payment_date: date, due_date: inApril ? '2026-04-10' : date,
  //     advance: adv,
  //     period_seq: inApril ? 1 : null,
  //     period_label: inApril ? 'April 2026' : '',
  //     period_seqs: inApril ? [1] : [],
  //     covers_full: paid > 0 && (due - paid - conc) <= 0,
  //     payment_mode: mode,
  //     payment_verified: true,
  //     cheque_dd_no:'', bank_name:'',
  //     items:[{ fee_head_code:1, fee_head_name:'Fee Bundle', due_amount: due, paid_amount: paid, concession: conc, balance: due - paid - conc }],
  //     total_due: due, total_paid: paid, total_concession: conc, balance: due - paid - conc,
  //     status, remarks:'', created_by:'Admin',
  //     late_fee: 0, days_overdue: 0,
  //     refund_amount: 0, refund_date: '', refund_reason: '', refund_mode: '',
  //   };
  // }

  // private s003PartialReceipt(): FeeReceipt {
  //   const base = this.mkReceipt(3,'RCP-2526-0003','S003','Rahul Kumar','Class IX','9-A','2026-27','2026-04-07','Cash',18600,15000,500,'Partial');
  //   return {
  //     ...base,
  //     covers_full: false,
  //     items: [
  //       { fee_head_code:2, fee_head_name:'Admission Fee',         due_amount:1500,  paid_amount:1500,  concession:0,   balance:0,    period_label:'April 2025' },
  //       { fee_head_code:1, fee_head_name:'Tuition Fee',           due_amount:15000, paid_amount:13500, concession:500, balance:1000, period_label:'April 2025' },
  //       { fee_head_code:8, fee_head_name:'Examination Fee',       due_amount:1000,  paid_amount:0,     concession:0,   balance:1000, period_label:'April 2025' },
  //       { fee_head_code:3, fee_head_name:'Library Fee',           due_amount:500,   paid_amount:0,     concession:0,   balance:500,  period_label:'April 2025' },
  //       { fee_head_code:5, fee_head_name:'Sports & Activity Fee', due_amount:600,   paid_amount:0,     concession:0,   balance:600,  period_label:'April 2025' },
  //     ],
  //   };
  // }

  //--- Actions ----------------------------
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

  setRecNo() {
    return `RCP-2026-${String(this.nextReceiptId).padStart(4, '0')}`;
  }
}
